import { CopayProcFeeDto } from "src/app/core/models/http/treatment/copay-procedure-fees/copay-proc-fee-dto";
import { ProcedureCodeDto } from "../../../../core/models/http/procedures/procedure-code-dto";
import { ExpenseFeeDto } from "../../../../core/models/http/treatment/fees/expense-fee-dto";
import { TreatmentFeeDto } from "../../../../core/models/http/treatment/fees/treatment-fee-dto";
import { TreatmentOptionFeeDto } from "../../../../core/models/http/treatment/fees/treatment-option-fee-dto";
import { TreatmentPlanDto } from "../../../../core/models/http/treatment/treatment-plans/treatment-plan-dto";
import { CopayProcedureFee } from "../copay-procedure-fees/copay-procedure-fee";
import { ExpenseFee } from "./models/expense-fee";
import { Fee } from "./models/fee";
import { TreatmentFee } from "./models/treatment-fee";
import { TreatmentOptionFee } from "./models/treatment-option-fee";

export const mapProcedure = (procedure: ProcedureCodeDto): number =>
  procedure ? procedure.Id : null;

export const mapTreatmentFee = (
  treatmentPlanId: number,
  treatment: TreatmentFeeDto,
  includedExpenseTotal: number
): TreatmentFee => ({
  description: treatment.Description,
  feeType: "Treatment",
  id: treatment.Id,
  practiceAmount: treatment.PracticeAmount + includedExpenseTotal,
  procedureId: mapProcedure(treatment.Procedure),
  treatmentId: treatment.TreatmentId,
  treatmentPlanId,
});

export const mapTreatmentOption = (
  treatmentPlanId: number,
  treatmentOption: TreatmentOptionFeeDto
): TreatmentOptionFee => ({
  description: treatmentOption.Description,
  feeType: "TreatmentOption",
  id: treatmentOption.Id,
  practiceAmount: treatmentOption.PracticeAmount,
  procedureId: mapProcedure(treatmentOption.Procedure),
  treatmentOptionId: treatmentOption.TreatmentOptionId,
  treatmentPlanId,
});

export const mapExpense = (
  treatmentPlanId: number,
  expense: ExpenseFeeDto
): ExpenseFee => ({
  description: expense.Description,
  feeType: "Expense",
  id: expense.Id,
  practiceAmount: expense.PracticeAmount,
  procedureId: mapProcedure(expense.Procedure),
  included: expense.Included,
  treatmentPlanId,
  practiceFeeId: expense.PracticeFeeId,
});

export const mapFees = (tp: TreatmentPlanDto) => {
  const includedExpenseTotal = tp.Expenses.reduce(
    (pv, cv) => pv + (cv.Included ? cv.PracticeAmount : 0),
    0
  );
  return [
    mapTreatmentFee(tp.TreatmentPlanId, tp.TreatmentFee, includedExpenseTotal),
    mapTreatmentOption(tp.TreatmentPlanId, tp.TreatmentOptionFee),
    ...tp.Expenses.map((e) => mapExpense(tp.TreatmentPlanId, e)),
  ];
};

export const computeTreatmentTotal = (fees: Fee[]): number => {
  return fees.reduce(
    (pv, cv) =>
      pv +
      ((cv.feeType === "Expense" && cv.included) || cv.feeType === "Treatment"
        ? cv.practiceAmount
        : 0),
    0
  );
};

export const mapCopayProcedureFees = (
  copayProcFees: CopayProcFeeDto[]
): CopayProcedureFee[] => {
  const results: CopayProcedureFee[] = [];

  for (const dto of copayProcFees) {
    results.push({
      children: mapCopayProcedureFees(dto.Children),
      copayAmount: dto.CopayAmount,
      copayFeeScheduleId: dto.CopayFeeScheduleId,
      copayProcFeeId: dto.CopayProcFeeId,
      description: dto.Description,
      feeType: dto.FeeType,
      id: `${dto.CopayFeeScheduleId}_${dto.FeeType}_${dto.PracticeFeeId}`,
      name: dto.Name,
      practiceAmount: dto.PracticeAmount,
      practiceFeeId: dto.PracticeFeeId,
      procedure: {
        category: dto.Procedure.Category ?? null,
        code: dto.Procedure.Code,
        id: dto.Procedure.Id,
      },
    });
  }

  return results;
};
